/**
 * Sentry sampling configuration for packages/admin
 * Shared between server and client instrumentation
 */
import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/nextjs"

/** Type for Sentry's tracesSampler sampling context */
type SamplingContext = Parameters<
  NonNullable<BrowserOptions["tracesSampler"]>
>[0]

/** Noise routes to drop completely (0% sampling) */
const NOISE_PATTERNS = [
  /_next\//,
  /\.(ico|png|jpg|jpeg|gif|svg|css|js|woff|ttf)$/,
  /\/(health|status|metrics|ready|live|ping)\b/,
  /favicon/,
]

/** Critical routes for admin app - orders, fulfillment, users (100% sampling) */
const CRITICAL_PATTERNS = [
  // Order management (highest priority)
  /\/orders/,
  /\/returned-orders/,
  /\/partner\/orders/,
  /\/todays-items/,
  // Fulfillment operations
  /\/fulfillment\//,
  // User management
  /\/users/,
  // Payments & billing
  /\/user-management\/post-payments/,
  /\/user-management\/.*invoices/,
  // Products (inventory)
  /\/products/,
  /\/user-products/,
]

/** Baseline sampling rate (55%) */
const BASELINE_RATE = 0.55

/** Check if transaction name matches any pattern */
function matchesAny(name: string, patterns: RegExp[]): boolean {
  return patterns.some((p) => p.test(name))
}

/**
 * Creates a tracesSampler function for Sentry
 * @param isProduction - Whether the app is running in production
 * @param isClient - Whether this is running on the client (enables window.location fallback)
 */
export function createTracesSampler({
  isProduction,
  isClient = false,
}: {
  isProduction: boolean
  isClient?: boolean
}) {
  return (samplingContext: SamplingContext) => {
    // Sentry v10+ uses samplingContext.name directly
    let name = samplingContext.name || ""

    // Client-side fallback to current pathname
    if (!name && isClient && typeof window !== "undefined") {
      name = window.location.pathname
    }

    // Always filter noise routes regardless of environment
    if (matchesAny(name, NOISE_PATTERNS)) return 0

    // Always boost critical routes regardless of environment
    if (matchesAny(name, CRITICAL_PATTERNS)) return 1.0

    // Non-production: reduced baseline (10%) to protect quota
    if (!isProduction) return 0.1

    return BASELINE_RATE
  }
}

/** Error patterns to drop (noisy, low-value errors) */
const IGNORED_ERROR_PATTERNS = [
  // Network errors from client disconnects
  /AbortError/i,
  // Browser extensions
  /chrome-extension:/i,
  /moz-extension:/i,
  // Next.js navigation to same URL https://sentry.io/answers/handle-hard-navigation-errors-in-nextjs/
  /Invariant: attempted to hard navigate to the same URL/i,
]

/**
 * Filter noisy errors before sending to Sentry.
 * Returns null to drop the event, or the event to send it.
 */
export function createBeforeSend() {
  return (event: ErrorEvent, _hint: EventHint): ErrorEvent | null => {
    const message = event.message || event.exception?.values?.[0]?.value || ""

    if (IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
      return null
    }

    return event
  }
}
